/**
 * /app/privacy — DSAR self-service (cross-trust, per §13.1).
 *
 * POPIA s24 data-subject rights — wired to the backend DSAR API:
 *
 *   - "Request my data"  → POST /v1/dsar { requestType: 'access' }
 *   - "Correct information" → POST /v1/dsar { requestType: 'correction' }
 *   - "Delete my account" → POST /v1/dsar { requestType: 'deletion' }
 *     (requires the high-friction "type 'delete my account'" confirmation)
 *
 *   - "Download audit trail" → fetches the caller's tenant-scoped audit-log
 *     via the existing useAuditLog hook and saves it as CSV in the browser.
 *     The user-facing /v1/audit-log endpoint is the right shape here —
 *     the admin-only /v1/audit/export endpoint requires step-up + compliance
 *     role.
 *     ⚠ WHAT THIS DOWNLOAD IS, EXACTLY (E2E 2026-08-21): the AUDIT_CSV_LIMIT
 *     most recent events, newest first, actors pseudonymised. It is assembled
 *     client-side, so it is NOT signed and NOT independently hash-verifiable —
 *     the card + modal copy now say so instead of promising "a full, signed,
 *     tamper-evident record". See the note above DSAR_CARDS.
 *
 * Previously-submitted requests are pulled from useDsarList() so the table
 * survives a reload; in-flight optimistic state comes from the mutation hook.
 */

import { useState } from 'react';
import { displayPersonName } from '../../lib/format/person-name';
import { createFileRoute } from '@tanstack/react-router';
import { ScreenShell } from '../../components/screens/screen-shell';
import { StateCard } from '../../components/state-card';
import { BleedTable, type BleedColumn, type BleedRow } from '../../components/bleed-table';
import { Icon } from '../../components/icon';
import type { IconName } from '../../components/icon';
import { Modal } from '../../components/ui/modal';
import { useToastStore } from '../../lib/toast-store';
import { useCreateDsar, useDsarList, type DsarRequestType, type DsarRequestView } from '../../lib/api/dsar';
import { useAuditLog, type AuditLogView } from '../../lib/api/audit-log';
import { useTenantPopiaOfficers, type PopiaOfficer } from '../../lib/api/popia-officers';
import { mapError } from '../../lib/error-messages';

export const Route = createFileRoute('/app/privacy')({
  component: PrivacyScreen,
});

// ── UI-side card kind. Maps onto a backend DsarRequestType for the three
// "submit" actions; 'audit' is a pure client-side download. ────────────────

type DsarUiKind = 'export' | 'correct' | 'delete' | 'audit';

/** Rows the audit-trail download asks the API for. The backend caps a page at
 *  500 (`listForTenantPage` clamps `limit`), so asking for more would only make
 *  the copy lie again — this constant is quoted VERBATIM in the card + modal
 *  text so the number the user reads is the number we actually request. Rows
 *  come back newest-first (`ORDER BY c.ts DESC`, fixed 2026-08-21), so the cap
 *  keeps the RECENT history rather than an arbitrary slice. */
const AUDIT_CSV_LIMIT = 500;

const REQUEST_TYPE_BY_KIND: Record<Exclude<DsarUiKind, 'audit'>, DsarRequestType> = {
  export: 'access',
  correct: 'correction',
  delete: 'deletion',
};

interface DsarCard {
  icon: IconName;
  title: string;
  subtitle: string;
  body: string;
  cta: string;
  ctaTone: 'primary' | 'danger' | 'neutral';
  modalTitle: string;
  modalBody: string;
  kind: DsarUiKind;
}

const DSAR_CARDS: DsarCard[] = [
  {
    icon: 'doc',
    title: 'Request my data',
    subtitle: 'POPIA s24a: data access',
    body: 'Receive a complete export of all personal data Elreign holds about you, including trust records, documents, activity logs, and contact information, compiled and delivered within 30 days.',
    cta: 'Request data export',
    ctaTone: 'primary',
    kind: 'export',
    modalTitle: 'Request data export',
    modalBody: 'A JSON export of all personal data Elreign holds will be generated and sent to your registered email address within 30 days. This includes trust records, documents, activity logs, and contact information.',
  },
  {
    icon: 'search',
    title: 'Correct information',
    subtitle: 'POPIA s24b: rectification',
    body: 'If any personal information held about you is inaccurate, incomplete, misleading, or out of date, you may request that it be corrected. A brief description of the correction needed is sufficient.',
    cta: 'Submit correction request',
    ctaTone: 'neutral',
    kind: 'correct',
    modalTitle: 'Submit correction request',
    modalBody: 'Your correction request will be reviewed by the Elreign Information Officer within 30 days. You will receive a confirmation email with your reference number.',
  },
  {
    icon: 'shield',
    title: 'Delete my account',
    subtitle: 'POPIA s24: erasure',
    body: 'You may request deletion of your account and associated personal data. Note: data directly tied to trust records and FICA obligations cannot be deleted until the FICA s23 5-year retention period expires. This action triggers an admin Tier 3 review.',
    cta: 'Request account deletion',
    ctaTone: 'danger',
    kind: 'delete',
    modalTitle: 'Request account deletion',
    modalBody: 'Account deletion is subject to FICA s23 retention requirements. Data linked to active trust records will be retained for the mandatory 5-year period. Once reviewed and approved, your account will be closed and personal data will be deleted where legally permissible. This action triggers a Tier 3 admin review.',
  },
  // ⚠ THE COPY BELOW IS DELIBERATELY MODEST (E2E 2026-08-21). It used to
  // promise "a full record of all data access events … signed and
  // tamper-evident" and "a signed CSV of all data access events". Three of
  // those claims were false:
  //   - NOT full — the request is capped at AUDIT_CSV_LIMIT rows.
  //   - NOT signed — the CSV is assembled in this browser from a JSON
  //     response. Nothing signs it; anyone can edit it in a spreadsheet.
  //   - NOT independently tamper-evident — each row carries prevHash/hash, but
  //     the payload those hashes were computed over includes the raw subjectId,
  //     which is withheld for POPIA pseudonymisation. A recipient holding only
  //     this file CANNOT recompute a hash and check it. The hashes prove the
  //     rows are contiguous and unreordered RELATIVE TO EACH OTHER, and that is
  //     the whole of what they prove here.
  // Telling a data subject their file is tamper-evident when it is not is worse
  // than giving them no file. Say exactly what it is.
  {
    icon: 'audit',
    title: 'Download audit trail',
    subtitle: 'Your data activity log',
    body: `Download the ${AUDIT_CSV_LIMIT} most recent activity events recorded against your account and trusts: what happened, when, and the role that did it. People are identified by a pseudonym, not by name, so the file stays safe to share.`,
    cta: 'Download audit trail',
    ctaTone: 'neutral',
    kind: 'audit',
    modalTitle: 'Download audit trail',
    modalBody: `A CSV of your ${AUDIT_CSV_LIMIT} most recent activity events will be downloaded, newest first. Each row carries the hash linking it to the event before it, so you can see the sequence is unbroken. Actors appear as pseudonyms rather than names. This file is not a signed or independently verifiable copy of the record. For that, ask your Information Officer for the full audit export.`,
  },
];

const KIND_LABEL: Record<DsarUiKind, string> = {
  export: 'Data access request',
  correct: 'Correction request',
  delete: 'Account deletion request',
  audit: 'Audit trail download',
};

const REQUEST_TYPE_LABEL: Record<DsarRequestType, string> = {
  access: 'Data access request',
  correction: 'Correction request',
  deletion: 'Account deletion request',
};

const STATUS_LABEL: Record<DsarRequestView['status'], string> = {
  received: 'Received',
  'identity-verified': 'Identity verified',
  scoped: 'Scoped',
  'holds-checked': 'Holds checked',
  'cooling-off': 'Cooling off',
  // Erasure-quorum collection state (F-018) — surfaced to the data subject
  // as plain "approvals in progress" language.
  'awaiting-approvals': 'Erasure approvals in progress',
  fulfilled: 'Completed',
  rejected: 'Rejected',
  'held-pending-legal-clearance': 'On legal hold',
  '4-eye-approved': 'Four-eye approved',
};

// ── Previous requests, as a BleedTable (cohesion pass 2026-09-15) ──────────
//
// The status chip is SENTENCE CASE now (design gate D2 / contract 1b): the
// uppercase-plus-tracked-letter-spacing treatment it carried before was one
// of the 168 inline uppercase chips the gap analysis counted, and
// STATUS_LABEL's own values ("Identity verified", "Cooling off") were
// already written in sentence case — only the CSS was shouting.
const dsarColumns: BleedColumn[] = [
  { key: 'type', header: 'Request type' },
  { key: 'submitted', header: 'Submitted' },
  { key: 'reference', header: 'Reference' },
  { key: 'status', header: 'Status' },
  { key: 'export', header: 'Export this request', kind: 'action' },
];

function dsarRow(req: DsarRequestView): BleedRow {
  const isFulfilled = req.status === 'fulfilled';
  const isRejected = req.status === 'rejected' || req.status === 'held-pending-legal-clearance';
  return {
    key: req.id,
    cells: {
      type: (
        // Roman, not the entity class: a request TYPE is a category, not a
        // named thing, so italic serif does not apply here (design gate D2).
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
            {REQUEST_TYPE_LABEL[req.requestType]}
          </span>
          <span className="bleed-table-sub">POPIA s24: {req.requestType}</span>
        </span>
      ),
      submitted: <span style={{ color: 'var(--ink-2)', fontSize: 13.5 }}>{formatDate(req.receivedAt)}</span>,
      reference: (
        <span className="bleed-table-mono" title={req.id} style={{ fontSize: 12.5 }}>
          {dsarHumanReference(req)}
        </span>
      ),
      status: (
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 999,
            background: isFulfilled ? 'var(--positive-soft)' : isRejected ? 'var(--negative-soft)' : 'var(--accent-100)',
            color: isFulfilled ? 'var(--positive)' : isRejected ? 'var(--negative)' : 'var(--accent-700)',
          }}
        >
          {STATUS_LABEL[req.status]}
        </span>
      ),
      export: <ExportDownload req={req} />,
    },
  };
}

// ── Audit trail CSV builder ──────────────────────────────────────────────

function toCsvCell(value: unknown): string {
  const s = value === undefined || value === null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildAuditCsv(events: AuditLogView[]): string {
  // `prev_hash` + `hash` let the recipient check chain CONTINUITY — each row's
  // prev_hash must equal the previous row's hash — which detects a deleted or
  // reordered row within the file. They do NOT let the recipient recompute a
  // hash and confirm a row's contents: the hashed payload includes the raw
  // subjectId, deliberately withheld for POPIA pseudonymisation. The card copy
  // above states that limit rather than claiming "tamper-evident".
  // ID-safe: the data subject is identified only by `subject_id_hash` — never
  // the raw subjectId / id / tenantId.
  const header = ['timestamp', 'action', 'subject_type', 'subject_id_hash', 'performed_by_role', 'correlation_id', 'prev_hash', 'hash'];
  const rows = [header.join(',')];
  for (const e of events) {
    rows.push([
      toCsvCell(e.ts),
      toCsvCell(e.action),
      toCsvCell(e.subjectType),
      toCsvCell(e.subjectIdHash),
      toCsvCell(e.performedByRole),
      toCsvCell(e.correlationId ?? ''),
      toCsvCell(e.prevHash),
      toCsvCell(e.hash),
    ].join(','));
  }
  return rows.join('\n');
}

function downloadAuditCsv(events: AuditLogView[]) {
  const csv = buildAuditCsv(events);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `elreign-audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Date formatter ───────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Subject-access export download affordance ────────────────────────────
//
// Once the Information Officer generates the POPIA s23(1) bundle
// (POST /v1/admin/dsar/{id}/export), the DSAR row carries `accessExport`
// with a time-limited read-only link. Three honest states:
//   - live link    → "Download export" + size + link expiry
//   - expired link → tell the user to ask for a regeneration (no dead link)
//   - url === null → render nothing (delivery not configured — the backend
//     withheld the link, so we never show a broken affordance)

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ExportDownload({ req, now }: { req: DsarRequestView; now?: () => number }) {
  const exp = req.accessExport;
  if (req.requestType !== 'access' || !exp || !exp.url) return null;

  const nowMs = (now ?? Date.now)();
  const expired = exp.expiresAt !== null && new Date(exp.expiresAt).getTime() <= nowMs;

  if (expired) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
        Export link expired. Contact your Information Officer to regenerate it.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <a
        href={exp.url}
        target="_blank"
        rel="noopener noreferrer"
        download={`elreign-data-export-${req.id}.json`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12.5, fontWeight: 600, color: 'var(--primary-800)',
          background: 'var(--primary-100)', padding: '5px 12px', borderRadius: 999,
          textDecoration: 'none',
        }}
      >
        Download export
      </a>
      <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--ink-3)' }}>
        JSON &middot; {formatBytes(exp.sizeBytes)}
        {exp.expiresAt ? <> &middot; link valid until {formatDate(exp.expiresAt)}</> : null}
      </div>
    </div>
  );
}

// ── DSAR human-readable reference ────────────────────────────────────────
//
// 2026-05-20 (Batch 7 / F02-P-062): the DSAR table reference column
// rendered `{req.id.slice(0, 16)}…` — sixteen characters of a UUID,
// which is meaningless to the user and exposes Cosmos implementation
// detail. The backend doesn't (yet) mint a human reference field on
// DsarRequestView, so derive a deterministic one from the request's
// receivedAt date + a stable 4-char suffix off the UUID itself. The
// shape is `DSAR-{YYYY}-{MM}-{DD}-{abcd}` — a fiduciary can read it
// aloud, but it still globally identifies the row.

function dsarHumanReference(req: DsarRequestView): string {
  const d = new Date(req.receivedAt);
  if (Number.isNaN(d.getTime())) {
    return `DSAR-${req.id.slice(0, 4)}`;
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  // Deterministic 4-char hex tail off the raw id — strips dashes so the
  // result is always alphanumeric. Falls back to the first 4 raw chars
  // when the id is shorter than 4 hex digits (defence — should not
  // happen with Cosmos UUIDs).
  const hex = req.id.replace(/[^a-fA-F0-9]/g, '').slice(0, 4) || req.id.slice(0, 4);
  return `DSAR-${yyyy}-${mm}-${dd}-${hex}`;
}

// ── POPIA officers section ───────────────────────────────────────────────

interface PopiaOfficerCardProps {
  /** Header text — "Information Officer" or "Deputy Information Officer". */
  role: string;
  /** Officer record, or null when undesignated. */
  officer: PopiaOfficer | null;
  /** Optional fallback string for the empty state (e.g. "Not designated"). */
  emptyLabel?: string;
  /** When true, the placeholder skeleton is rendered instead of empty state. */
  loading?: boolean;
}

function PopiaOfficerCard({ role, officer, emptyLabel, loading }: PopiaOfficerCardProps) {
  if (loading) {
    return (
      <div className="card" aria-busy="true">
        <div className="eyebrow ink" style={{ marginBottom: 10 }}>
          {role}
        </div>
        <div style={{ height: 18, width: '60%', background: 'var(--surface-2)', borderRadius: 4, marginBottom: 8 }} />
        <div style={{ height: 14, width: '80%', background: 'var(--surface-2)', borderRadius: 4 }} />
      </div>
    );
  }

  if (!officer) {
    return (
      <div className="card">
        <div className="eyebrow ink" style={{ marginBottom: 10 }}>
          {role}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.65 }}>
          {emptyLabel ?? 'Not designated.'}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="eyebrow ink" style={{ marginBottom: 10 }}>
        {role}
      </div>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 14, rowGap: 6 }}>
        <dt style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Name</dt>
        <dd style={{ margin: 0, fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{displayPersonName(officer.displayName)}</dd>
        <dt style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Email</dt>
        <dd style={{ margin: 0, fontSize: 13.5 }}>
          {officer.contactEmail ? (
            <a className="mono" href={`mailto:${officer.contactEmail}`} style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>
              {officer.contactEmail}
            </a>
          ) : (
            <span style={{ color: 'var(--ink-3)' }}>Contact via the firm.</span>
          )}
        </dd>
      </dl>
    </div>
  );
}

interface PopiaOfficersSectionProps {
  loading: boolean;
  errored: boolean;
  io: PopiaOfficer | null;
  deputy: PopiaOfficer | null;
}

function PopiaOfficersSection({ loading, errored, io, deputy }: PopiaOfficersSectionProps) {
  // Loading: render both cards as skeletons. Don't gate on tenant query
  // separately — the hook waits on it internally and returns isLoading=true.
  if (loading) {
    return (
      <section aria-labelledby="popia-io-heading" style={{ marginBottom: 28 }}>
        <h2
          id="popia-io-heading"
          className="serif-heading"
          style={{ fontWeight: 400, fontSize: 22, margin: '0 0 6px' }}
        >
          Information Officer
        </h2>
        <p
          id="popia-io-explainer"
          style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px', maxWidth: 720 }}
        >
          POPIA s55-56 requires every responsible party to designate an Information Officer (and may designate one or more Deputies) as the point of contact for data subject rights requests, complaints, and Regulator engagement.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} aria-describedby="popia-io-explainer">
          <PopiaOfficerCard role="Information Officer" officer={null} loading />
          <PopiaOfficerCard role="Deputy Information Officer" officer={null} loading />
        </div>
      </section>
    );
  }

  // Errored or both null: surface the regulator-required placeholder.
  if (errored || (!io && !deputy)) {
    return (
      <section aria-labelledby="popia-io-heading" style={{ marginBottom: 28 }}>
        <h2
          id="popia-io-heading"
          className="serif-heading"
          style={{ fontWeight: 400, fontSize: 22, margin: '0 0 6px' }}
        >
          Information Officer
        </h2>
        <p
          id="popia-io-explainer"
          style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px', maxWidth: 720 }}
        >
          POPIA s55-56 requires every responsible party to designate an Information Officer (and may designate one or more Deputies) as the point of contact for data subject rights requests, complaints, and Regulator engagement.
        </p>
        <div
          className="card"
          aria-describedby="popia-io-explainer"
          style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.65 }}
        >
          Information Officer details pending designation.
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="popia-io-heading" style={{ marginBottom: 28 }}>
      <h2
        id="popia-io-heading"
        className="serif-heading"
        style={{ fontWeight: 400, fontSize: 22, margin: '0 0 6px' }}
      >
        Information Officer
      </h2>
      <p
        id="popia-io-explainer"
        style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px', maxWidth: 720 }}
      >
        POPIA s55-56 requires every responsible party to designate an Information Officer (and may designate one or more Deputies) as the point of contact for data subject rights requests, complaints, and Regulator engagement.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} aria-describedby="popia-io-explainer">
        <PopiaOfficerCard role="Information Officer" officer={io} />
        <PopiaOfficerCard
          role="Deputy Information Officer"
          officer={deputy}
          emptyLabel="No Deputy currently designated."
        />
      </div>
    </section>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────

function PrivacyScreen() {
  const { pushToast } = useToastStore();
  const dsarListQ = useDsarList();
  const createDsar = useCreateDsar();
  const auditQ = useAuditLog({ limit: AUDIT_CSV_LIMIT, enabled: false });
  const officersQ = useTenantPopiaOfficers();

  const [activeCard, setActiveCard] = useState<DsarCard | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const DELETE_CONFIRM_STRING = 'delete my account';

  const requests = dsarListQ.data?.dsarRequests ?? [];

  function close() {
    setActiveCard(null);
    setDeleteConfirmText('');
  }

  async function handleConfirm() {
    if (!activeCard) return;

    if (activeCard.kind === 'audit') {
      try {
        const res = await auditQ.refetch();
        if (res.error) throw res.error;
        const events = res.data?.events ?? [];
        downloadAuditCsv(events);
        // The API reports `truncated` when more history exists than the page
        // returned (backend `listForTenantPage` over-fetches one row to detect
        // it). Read it structurally — the shared `AuditLogListResponse` type
        // does not declare the field, and widening a shared API type is not
        // this screen's call — but SAY SO when it is set, because a data
        // subject who is handed a partial record and told nothing will read it
        // as complete.
        const truncated =
          (res.data as { truncated?: boolean } | undefined)?.truncated === true;
        pushToast({
          kind: 'success',
          message: truncated
            ? `Downloaded your ${events.length} most recent events. Older history exists beyond this file. Ask your Information Officer for the full export.`
            : `Audit trail downloaded to your device (${events.length} event${events.length === 1 ? '' : 's'}).`,
        });
      } catch (e) {
        pushToast({ kind: 'error', message: mapError(e, 'load') });
      }
      close();
      return;
    }

    if (activeCard.kind === 'delete' && deleteConfirmText.trim().toLowerCase() !== DELETE_CONFIRM_STRING) {
      // Defence in depth — the submit button is disabled below, but if a
      // keyboard "Enter" lands here we still refuse.
      return;
    }

    const requestType = REQUEST_TYPE_BY_KIND[activeCard.kind];
    try {
      const res = await createDsar.mutateAsync({ requestType, reason: KIND_LABEL[activeCard.kind] });
      pushToast({
        kind: 'success',
        message: 'Request submitted. The Information Officer will respond within 30 days under POPIA s24.',
        ref: res.dsarRequest.id,
      });
      close();
    } catch (e) {
      pushToast({ kind: 'error', message: mapError(e, 'submit-request') });
    }
  }

  // Map a UI card kind to the most recent matching backend request, so the
  // card can render a "Request submitted · {id}" pill in-place.
  function latestForKind(kind: DsarUiKind): DsarRequestView | undefined {
    if (kind === 'audit') return undefined;
    const reqType = REQUEST_TYPE_BY_KIND[kind];
    return [...requests].reverse().find((r) => r.requestType === reqType);
  }

  const isAuditBusy = activeCard?.kind === 'audit' && auditQ.isFetching;
  const isSubmitBusy = activeCard?.kind !== 'audit' && createDsar.isPending;
  const deleteOk = activeCard?.kind !== 'delete'
    || deleteConfirmText.trim().toLowerCase() === DELETE_CONFIRM_STRING;

  return (
    <ScreenShell
      activeNav="privacy"
      eyebrow="Data rights"
      title="Privacy & data"
      // The Information Officer's contact details are rendered by the
      // dedicated PopiaOfficersSection below (sourced from
      // useTenantPopiaOfficers). The topbar sub line stays generic so
      // we don't duplicate — or, worse, contradict — that designation.
      sub="POPIA s24 data subject rights"
    >
      <PopiaOfficersSection
        loading={officersQ.isLoading}
        errored={officersQ.isError}
        io={officersQ.data?.informationOfficer ?? null}
        deputy={officersQ.data?.deputyInformationOfficer ?? null}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        {DSAR_CARDS.map((card) => {
          const lastSubmitted = latestForKind(card.kind);

          return (
            <div
              key={card.title}
              className="card"
              style={{ borderColor: card.ctaTone === 'danger' ? 'var(--negative-soft)' : undefined }}
            >
              {/* Icon + heading */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: card.ctaTone === 'danger' ? 'var(--negative-soft)' : card.ctaTone === 'primary' ? 'var(--primary-100)' : 'var(--surface-2)',
                  color: card.ctaTone === 'danger' ? 'var(--negative)' : card.ctaTone === 'primary' ? 'var(--primary-800)' : 'var(--ink-2)',
                  display: 'grid', placeItems: 'center',
                }}>
                  <Icon name={card.icon} size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>{card.title}</div>
                  <div className="eyebrow ink">{card.subtitle}</div>
                </div>
              </div>

              {/* Body */}
              <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 20, paddingBottom: 18, borderBottom: '1px solid var(--hairline)' }}>
                {card.body}
              </div>

              {/* Submitted reference pill */}
              {lastSubmitted && (
                <div
                  className="mono"
                  title={lastSubmitted.id}
                  style={{ fontSize: 12, color: 'var(--positive)', background: 'var(--positive-soft)', padding: '5px 10px', borderRadius: 6, marginBottom: 10, display: 'inline-block' }}
                >
                  Request submitted · {dsarHumanReference(lastSubmitted)}
                </div>
              )}

              {/* CTA */}
              <button
                type="button"
                onClick={() => setActiveCard(card)}
                style={{
                  background: card.ctaTone === 'primary' ? 'var(--primary-700)' : card.ctaTone === 'danger' ? 'transparent' : 'transparent',
                  color: card.ctaTone === 'primary' ? 'var(--surface)' : card.ctaTone === 'danger' ? 'var(--negative)' : 'var(--ink-2)',
                  border: card.ctaTone === 'primary' ? 'none' : card.ctaTone === 'danger' ? '1px solid var(--negative-soft)' : '1px solid var(--hairline)',
                  padding: '10px 20px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                  width: '100%', textAlign: 'center',
                }}
              >
                {card.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* Status table — wired to useDsarList */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '22px 28px', borderBottom: '1px solid var(--hairline)' }}>
          <h3 className="serif-heading" style={{ fontWeight: 400, fontSize: 22, margin: 0 }}>Previous requests</h3>
        </div>
        {dsarListQ.isLoading ? (
          <div style={{ padding: '22px 28px' }}>
            <StateCard
              variant="loading"
              testId="dsar-loading"
              label="Previous requests"
              headline="Reading your previous data-subject access requests…"
            />
          </div>
        ) : dsarListQ.isError ? (
          <div style={{ padding: '22px 28px' }}>
            <StateCard
              variant="error"
              testId="dsar-error"
              label="Previous requests"
              headline={mapError(dsarListQ.error, 'load')}
            />
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '22px 28px' }}>
            <StateCard
              variant="empty"
              testId="dsar-empty"
              label="Previous requests"
              headline="No previous requests on record."
              detail="This is your first interaction with POPIA self-service."
            />
          </div>
        ) : (
          <BleedTable
            testId="dsar"
            label="Your previous data-subject access requests"
            columns={dsarColumns}
            rows={requests.map(dsarRow)}
            footer={
              <>
                <span />
                <span>
                  {requests.length} request{requests.length !== 1 ? 's' : ''} on record
                </span>
              </>
            }
          />
        )}
      </div>

      {/*
        Information Officer contact + postal address live in the
        PopiaOfficersSection (sourced from useTenantPopiaOfficers).
        TODO(backend): wire tenant postal address via a tenant-postal-address
        endpoint when one exists. Until then we keep this footer scoped to
        the regulator-escalation cue only — never invent a tenant's address.
      */}
      <div style={{ marginTop: 20, padding: '18px 22px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        All POPIA requests processed within 30 days. Complaints may be escalated to the Information Regulator of South Africa.
      </div>

      {/* Confirmation modal */}
      <Modal
        open={activeCard !== null}
        onClose={close}
        title={activeCard?.modalTitle ?? ''}
        accentBorder={activeCard?.ctaTone === 'danger' ? 'var(--negative-soft)' : undefined}
      >
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.65, margin: '0 0 4px' }}>
          {activeCard?.modalBody}
        </p>

        {activeCard?.kind === 'delete' && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
                To confirm, type <span className="mono" style={{ color: 'var(--ink)' }}>delete my account</span>
              </span>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="delete my account"
                autoComplete="off"
                aria-label="Confirm by typing: delete my account"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--hairline-2)',
                  background: 'var(--bg)',
                  color: 'var(--ink)',
                  fontFamily: 'var(--sans)',
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </label>
          </div>
        )}

        <Modal.Footer>
          <button
            type="button"
            onClick={close}
            style={{ background: 'transparent', border: '1px solid var(--hairline)', padding: '9px 18px', borderRadius: 9, fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer', color: 'var(--ink-2)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={(activeCard?.kind === 'delete' && !deleteOk) || isSubmitBusy || isAuditBusy}
            style={{
              background: activeCard?.ctaTone === 'danger' ? 'var(--negative)' : 'var(--primary-700)',
              color: 'var(--surface)', border: 'none', padding: '9px 18px', borderRadius: 9,
              fontFamily: 'var(--sans)', fontSize: 13.5, fontWeight: 600,
              cursor: (isSubmitBusy || isAuditBusy || (activeCard?.kind === 'delete' && !deleteOk)) ? 'not-allowed' : 'pointer',
              opacity: (activeCard?.kind === 'delete' && !deleteOk) ? 0.55 : 1,
            }}
          >
            {activeCard?.kind === 'audit'
              ? isAuditBusy ? 'Preparing CSV…' : 'Download audit trail'
              : isSubmitBusy ? 'Submitting…' : activeCard?.kind === 'delete' ? 'Submit deletion request' : 'Submit request'}
          </button>
        </Modal.Footer>
      </Modal>
    </ScreenShell>
  );
}
